import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { ProjectionAppStore } from '../../stores/projection-app.store';
import { SessionSupersededService } from '../session-superseded/session-superseded.service';
import {
  DEVICE_ID_HEADER,
  DeviceIdentityService,
  IS_PROJECTION_APP_HEADER,
  REGISTER_DEVICE_HEADER,
} from './device-identity.service';

/** The `code` horizon-service returns in its `ErrorResponse` body for a 409 rejection. */
const SESSION_SUPERSEDED_CODE = 'SESSION_SUPERSEDED';

/**
 * True when an error is horizon-service's single-active-session rejection — an HTTP 409
 * whose `ErrorResponse` body carries `code: "SESSION_SUPERSEDED"`.
 */
export function isSessionSuperseded(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) {
    return false;
  }
  return (error.error as { code?: unknown } | null)?.code === SESSION_SUPERSEDED_CODE;
}

/**
 * Single-active-session enforcement (see docs/plans/2026-07-29-single-active-session-design.md):
 * attaches this browser's device id to every horizon-service call in new-backend mode, and
 * raises the blocking "logged in elsewhere" dialog the moment ANY call comes back superseded —
 * not just the next poll. Legacy-mode requests and non-service URLs are never touched.
 *
 * Kill-switch: once `SessionSupersededService.superseded()` is true, every further
 * horizon-service call is blocked client-side rather than sent — including one carrying
 * `X-Register-Device`, so a superseded device can't reclaim the slot just by making its next
 * call. `SessionSupersededService.acknowledge()` flips `superseded()` back to `false` *before*
 * calling `LogoutService.logout()`, so that device's own cleanup calls (the one legitimate
 * reason to talk to the backend while superseded) are sent normally.
 */
export const deviceIdentityInterceptor: HttpInterceptorFn = (req, next) => {
  if (environment.useLegacyBackend === true) return next(req);
  if (!req.url.startsWith(environment.classviewServiceUrl)) return next(req);

  const deviceIdentity = inject(DeviceIdentityService);
  const sessionSuperseded = inject(SessionSupersededService);

  if (sessionSuperseded.superseded()) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 499,
          statusText: 'Session superseded — request blocked client-side',
          url: req.url,
        }),
    );
  }

  // Single-active-session enforcement: registration used to only ever happen inside
  // POST /v1/classes (a normal class-picker load). A device that already has a class selected —
  // a reload, a bookmarked deep link, resuming a tab — never calls that endpoint at all, so it
  // never got a chance to claim the active-device slot. Sending this on the first
  // horizon-service call of a fresh session, whatever endpoint that happens to be, closes that
  // gap — see DeviceIdentityService#consumeRegistrationPending.
  const isProjectionApp =
    inject(ProjectionAppStore).isProjectionApp() || req.headers.has(IS_PROJECTION_APP_HEADER);
  const shouldRegister = !isProjectionApp && deviceIdentity.consumeRegistrationPending();

  const withDeviceId = req.clone({
    setHeaders: {
      [DEVICE_ID_HEADER]: deviceIdentity.deviceId(),
      ...(isProjectionApp ? { [IS_PROJECTION_APP_HEADER]: 'true' } : {}),
      ...(shouldRegister ? { [REGISTER_DEVICE_HEADER]: 'true' } : {}),
    },
  });

  return next(withDeviceId).pipe(
    catchError((error: unknown) => {
      if (isSessionSuperseded(error)) {
        sessionSuperseded.notifySuperseded();
      }
      return throwError(() => error);
    }),
  );
};
